<template>
  <div>
    <Section heading="Edit Member" :mode="mode" />
    <div class="grid gap-6 sm:gap-4">
      <AppCard
        :mode="mode"
        title="Name Settings"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <AppInput
            :mode="mode"
            id="company"
            title="Company"
            placeholder="Not specified"
            v-model="memberData.company_name"
          />
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Company Logo"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-3 gap-4 sm:grid-cols-1">
            <div class="col-span-1">
              <div
                class="flex h-[120px] items-center justify-center rounded-lg border"
                :class="[
                  mode === 'dark' ? 'border-dark-border' : 'border-border-10',
                ]"
              >
                <img
                  v-if="memberData.logo_url"
                  :src="memberData.logo_url"
                  alt="Company logo"
                  class="max-h-full max-w-full object-contain"
                />
                <span v-else class="text-[14px] text-black-60"
                  >No logo uploaded</span
                >
              </div>
            </div>
            <div class="col-span-2 grid gap-3 sm:col-span-1">
              <input
                type="file"
                ref="logoInput"
                accept="image/*"
                @change="onLogoChosen"
                class="text-[14px]"
              />
              <p class="text-[12px] text-black-60">
                Max 400×120, resized server-side. Stored as PNG. Click Update
                information below to keep the change.
              </p>
              <div class="flex gap-2">
                <AppButton
                  title="Upload"
                  @action="uploadLogo"
                  :isDisabled="!selectedLogo || logoUploading"
                />
                <AppButton
                  v-if="memberData.logo_url"
                  title="Remove"
                  type="danger"
                  @action="removeLogo"
                />
              </div>
            </div>
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Phone Numbers"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="phone1"
              type="tel"
              title="Phone 1"
              placeholder="Not specified"
              v-model="memberData.company_phone1"
            />
            <AppInput
              :mode="mode"
              id="phone2"
              type="tel"
              title="Phone 2"
              placeholder="Not specified"
              v-model="memberData.company_phone2"
            />
            <AppInput
              :mode="mode"
              id="fax"
              type="tel"
              title="Fax"
              placeholder="Not specified"
              v-model="memberData.fax"
            />
            <AppInput
              :mode="mode"
              id="toll"
              title="Toll free"
              placeholder="Not specified"
              v-model="memberData.toll_free_phone"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Address Information"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="address-1"
              title="Address 1"
              placeholder="Not specified"
              v-model="memberData.address1"
            />
            <AppInput
              :mode="mode"
              id="address-2"
              title="Address 2"
              placeholder="Not specified"
              v-model="memberData.address2"
            />
            <div class="col-span-2 sm:col-span-1">
              <div
                class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4"
              >
                <AppSelect
                  :mode="mode"
                  title="Country"
                  v-model="memberData.country"
                >
                  <template #options>
                    <option value="">Country</option>
                    <option
                      v-for="(country, key) in countries"
                      :value="country.code"
                      :key="key"
                      :selected="country.code === memberData.country"
                    >
                      {{ country.country }}
                    </option>
                  </template>
                </AppSelect>
                <AppInput
                  :mode="mode"
                  id="zip"
                  title="Zip"
                  placeholder="Not specified"
                  v-model="memberData.zip"
                />
                <AppSelect
                  :mode="mode"
                  title="City"
                  v-model="memberData.city"
                  :isDisabled="cities.length === 0"
                >
                  <template #options>
                    <option value="">City</option>
                    <option
                      v-for="(city, key) in cities"
                      :value="city.name"
                      :key="key"
                      :selected="city.name === memberData.city"
                    >
                      {{ city.name }}
                    </option>
                  </template>
                </AppSelect>
                <AppSelect
                  :mode="mode"
                  title="State"
                  v-model="memberData.state_region"
                  :isDisabled="states.length === 0"
                >
                  <template #options>
                    <option value="">State</option>
                    <option
                      v-for="(state, key) in states"
                      :value="state.code"
                      :key="key"
                      :selected="state.code === memberData.state_region"
                    >
                      {{ state.name }}
                    </option>
                  </template>
                </AppSelect>
              </div>
            </div>
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Other Communication Channels"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid gap-4">
            <AppInput
              :mode="mode"
              type="email"
              id="email"
              title="Email"
              placeholder="Not specified"
              v-model="memberData.company_email"
            />
            <AppInput
              :mode="mode"
              type="url"
              id="url"
              title="Website URL"
              placeholder="https://your-company.com"
              v-model="memberData.url"
            />
            <AppSwitchers
              v-model="checkboxes"
              :mode="mode"
              :modeType="'checkbox'"
              :isDisabled="!isAdmin"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Product Categories"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <CategoriesSpecialties
            :mode="mode"
            :data="memberData.product_categories"
            @selectedDataUpdated="updateSelectedProductCategories"
            :set="setData"
          />
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Specialties"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <CategoriesSpecialties
            :mode="mode"
            :data="memberData.specialties"
            @selectedDataUpdated="updateSelectedSpecialties"
            type="specialties"
            :set="setData"
          />
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Manufacturers we Specialize in"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div>
            <div class="relative z-20">
              <div class="relative flex items-center">
                <Search
                  :size="20"
                  :stroke-width="1.75"
                  class="absolute left-4 opacity-40"
                />
                <AppInput
                  :mode="mode"
                  type="search"
                  placeholder="Search by manufacturer"
                  v-model="mfr_company_query"
                  classes="!pl-11"
                  @focus="searchFocus = true"
                  :hasLabel="false"
                />
              </div>
              <div
                v-if="searchFocus"
                class="without-scrollbar absolute left-0 right-0 top-full z-10 mt-1 max-h-[210px] overflow-y-auto rounded-lg border p-4"
                :class="[
                  mode === 'dark'
                    ? 'border-dark-border bg-dark-sidebar'
                    : 'border-black-10 bg-white-100',
                ]"
              >
                <div
                  v-if="filteredManufactuers && filteredManufactuers.length > 0"
                  class="grid gap-3"
                >
                  <AppCheckbox
                    v-for="(mfr, key) in filteredManufactuers"
                    :key="key"
                    :mode="mode"
                    :id="mfr.mfr_id"
                    v-model="mfr.selected"
                    :title="mfr.company"
                  />
                </div>
                <div v-else class="text-center">
                  <span
                    class="text-sm font-medium"
                    :class="[
                      mode === 'dark' ? 'text-dark-text' : 'text-black-80',
                    ]"
                    >No manufacturers found to select</span
                  >
                </div>
              </div>
            </div>
            <div
              class="fixed inset-0 z-10"
              v-if="searchFocus"
              @click="searchFocus = false"
            ></div>
            <div v-if="selectedManufactuers.length > 0" class="mt-4">
              <div class="flex flex-wrap items-center gap-2">
                <span
                  class="cursor-pointer rounded-md border border-primary-100 px-3 py-1 text-[13px] font-medium text-primary-100 transition-colors hover:bg-primary-100 hover:text-white-100"
                  :class="[
                    mode === 'dark' ? 'bg-dark-sidebar' : 'bg-white-100',
                  ]"
                  v-for="(mfr, index) in selectedManufactuers"
                  @click="removeSelectedMfr(mfr.mfr_id)"
                  :key="index"
                >
                  {{ mfr.company }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </AppCard>
      <AppCard
        v-if="canManageContacts"
        :mode="mode"
        title="Contacts"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #actions>
          <AppButton
            title="Add new contact"
            @action="addContact"
            classes="!w-auto !px-4 sm:!h-10"
          />
        </template>
        <template #body>
          <template v-if="contacts && contacts.length">
            <AppTable :mode="mode" :columns="contactColumns">
              <template #rows>
                <tr v-for="(item, key) in contacts" :key="key">
                  <td>
                    <FallbackName
                      :to="{
                        name: 'contact-details',
                        params: { id: item.contact_id },
                      }"
                      :first="item.first_name"
                      :last="item.last_name"
                    />
                  </td>
                  <td>
                    <FallbackText :value="item.email" :isTruncate="false" />
                  </td>
                  <td>
                    <FallbackText :value="item.username" :isTruncate="false" />
                  </td>
                  <td>
                    <span v-if="item.mfrs">{{ item.mfrs.length }}</span>
                    <span v-else>0</span>
                  </td>
                  <td>
                    <div class="flex items-center gap-2">
                      <TableAction
                        :isActive="
                          item.ok_to_update || isAdmin || contact.administrator
                        "
                        :mode="mode"
                        @itemAction="
                          $redirectTo(
                            item.contact_id === contact.contact_id
                              ? 'edit-contact'
                              : 'contact-edit',
                            item.contact_id,
                          )
                        "
                        action="edit"
                      />
                      <TableAction
                        :mode="mode"
                        :isActive="item.contact_id !== contact.contact_id"
                        @itemAction="confirmDelete(item.contact_id)"
                        action="delete"
                      />
                    </div>
                  </td>
                </tr>
              </template>
            </AppTable>
            <MobileTable
              :mode="mode"
              :fields="{
                contact: 'Contact',
                email: 'Email',
                username: 'Username',
                manufacturers: 'Manufacturers',
                actions: 'Actions',
              }"
              :data="contacts"
            >
              <template #default="{ row, fieldKey }">
                <FallbackName
                  v-if="fieldKey === 'contact'"
                  :to="{
                    name: 'contact-details',
                    params: { id: row.first_name },
                  }"
                  :first="row.first_name"
                  :last="row.last_name"
                  :isTruncate="false"
                />
                <template v-else-if="fieldKey === 'email'">
                  <FallbackText :value="row.email" :isTruncate="false" />
                </template>
                <template v-else-if="fieldKey === 'username'">
                  <FallbackText :value="row.username" :isTruncate="false" />
                </template>
                <template v-else-if="fieldKey === 'manufacturers'">
                  <span v-if="row.mfrs">{{ row.mfrs.length }}</span>
                  <span v-else>0</span>
                </template>
                <template v-else-if="fieldKey === 'actions'">
                  <div class="flex items-center gap-2">
                    <TableAction
                      :isActive="
                        row.ok_to_update || isAdmin || contact.administrator
                      "
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          row.contact_id === contact.contact_id
                            ? 'edit-contact'
                            : 'contact-edit',
                          row.contact_id,
                        )
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      :isActive="row.contact_id !== contact.contact_id"
                      @itemAction="confirmDelete(row.contact_id)"
                      action="delete"
                    />
                  </div>
                </template>
              </template>
            </MobileTable>
          </template>
          <EmptyData v-else :notFound="'No contacts found!'" :mode="mode" />
        </template>
      </AppCard>
      <AppCard
        v-if="isAdmin"
        :mode="mode"
        title="Back Office"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="annual_dues"
              title="Annual dues"
              placeholder="Not specified"
              v-model="backOfficeMember.annual_dues"
            />
            <AppInput
              :mode="mode"
              id="banner_ad"
              title="Banner ad"
              placeholder="Not specified"
              v-model="backOfficeMember.banner_ad"
            />
            <AppInput
              :mode="mode"
              id="banner_image"
              title="Banner image"
              placeholder="Not specified"
              v-model="backOfficeMember.banner_image"
            />
            <AppInput
              :mode="mode"
              id="banner_url"
              type="url"
              title="Banner URL"
              placeholder="Not specified"
              v-model="backOfficeMember.banner_url"
            />
            <AppInput
              :mode="mode"
              id="cc_exp_date"
              type="date"
              title="CC exp date"
              placeholder="Not specified"
              v-model="backOfficeMember.cc_exp_date"
            />
            <AppInput
              :mode="mode"
              id="cc_last_four"
              title="CC last four"
              placeholder="Not specified"
              v-model="backOfficeMember.cc_last_four"
            />
            <AppInput
              :mode="mode"
              id="cc_name"
              title="CC name"
              placeholder="Not specified"
              v-model="backOfficeMember.cc_name"
            />
            <AppInput
              :mode="mode"
              id="cc_type"
              title="CC type"
              placeholder="Not specified"
              v-model="backOfficeMember.cc_type"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppTextarea
                :mode="mode"
                id="company_info"
                title="Company info"
                placeholder="Not specified"
                v-model="backOfficeMember.company_info"
              />
            </div>
            <div class="col-span-2 sm:col-span-1">
              <div
                class="grid grid-cols-3 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4"
              >
                <div
                  class="form-control flex items-center"
                  :class="[mode === 'dark' ? 'dark-form-control' : '']"
                >
                  <AppCheckbox
                    :mode="mode"
                    id="conference_member_rate"
                    v-model="backOfficeMember.conference_member_rate"
                    title="Conference member rate"
                  />
                </div>
                <div
                  class="form-control flex items-center"
                  :class="[mode === 'dark' ? 'dark-form-control' : '']"
                >
                  <AppCheckbox
                    :mode="mode"
                    id="ethics_non_participant"
                    v-model="backOfficeMember.ethics_non_participant"
                    title="Ethics non participant"
                  />
                </div>
                <div
                  class="form-control flex items-center"
                  :class="[mode === 'dark' ? 'dark-form-control' : '']"
                >
                  <AppCheckbox
                    :mode="mode"
                    id="ethics_violator"
                    v-model="backOfficeMember.ethics_violator"
                    title="Ethics violator"
                  />
                </div>
                <div
                  class="form-control flex items-center"
                  :class="[mode === 'dark' ? 'dark-form-control' : '']"
                >
                  <AppCheckbox
                    :mode="mode"
                    id="vendor"
                    v-model="memberData.vendor"
                    title="Vendor"
                  />
                </div>
              </div>
            </div>
            <AppInput
              :mode="mode"
              id="data_source"
              title="Data source"
              placeholder="Not specified"
              v-model="backOfficeMember.data_source"
            />
            <AppInput
              :mode="mode"
              id="networ_paid_thru"
              title="Network paid thru"
              placeholder="Not specified"
              v-model="backOfficeMember.networ_paid_thru"
            />
            <AppInput
              :mode="mode"
              id="network_amount"
              title="Network amount"
              placeholder="Not specified"
              v-model="backOfficeMember.network_amount"
            />
            <AppInput
              :mode="mode"
              id="network_start_date"
              type="date"
              title="Network start date"
              placeholder="Not specified"
              v-model="backOfficeMember.network_start_date"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppInput
                :mode="mode"
                id="vat_number"
                title="Vat number"
                placeholder="Not specified"
                v-model="backOfficeMember.vat_number"
              />
            </div>
            <div class="col-span-2 sm:col-span-1">
              <AppTextarea
                :mode="mode"
                id="notes"
                title="Notes"
                placeholder="Not specified"
                v-model="backOfficeMember.notes"
              />
            </div>
          </div>
        </template>
      </AppCard>
      <AppButton
        title="Update information"
        @action="handleMember"
        :isDisabled="!memberData.company_name"
      />
    </div>
    <AddContactSettings
      v-if="currentMember"
      :member="currentMember"
      :countries="countries"
      :mode="mode"
      @close="closeAddContact"
      @contactAdded="getMember"
    />
    <DeleteRecord
      :currentId="currentContactId"
      :isSimpleRemove="true"
      :mode="mode"
      @recordDeletedWithArrayRemove="recordDeleted"
      @cancelDelete="currentContactId = null"
    />
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import Section from "@C/Common/Section.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppTextarea from "@C/Form/AppTextarea.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppSwitchers from "@C/Common/AppSwitchers.vue";
import AppTable from "@C/Tables/AppTable.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import EmptyData from "@C/Common/EmptyData.vue";
import TableAction from "@C/Tables/TableAction.vue";
import FallbackName from "@C/Tables/FallbackName.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import AddContactSettings from "@C/AddContactSettings.vue";
import DeleteRecord from "@C/DeleteRecord.vue";
import CategoriesSpecialties from "@C/CategoriesSpecialties/CategoriesSpecialties.vue";

export default {
  name: "MemberActions",
  props: {
    mode: String,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  components: {
    Search,
    Section,
    AppCard,
    AppInput,
    AppTextarea,
    AppSelect,
    AppCheckbox,
    AppSwitchers,
    AppButton,
    AppTable,
    MobileTable,
    EmptyData,
    TableAction,
    FallbackName,
    FallbackText,
    AddContactSettings,
    DeleteRecord,
    CategoriesSpecialties,
  },
  data() {
    return {
      id: "",
      memberData: {
        address1: "",
        address2: "",
        assoc_member: false,
        assoc_status: "",
        city: "",
        company_email: "",
        company_name: "",
        company_phone1: "",
        company_phone2: "",
        country: "",
        fax: "",
        logo: "",
        logo_url: "",
        member_id: "",
        member_since: null,
        mfrs: [],
        mission: "",
        network_subscriber: false,
        password: null,
        state_region: "",
        technical_services: "",
        toll_free_phone: "",
        url: "",
        url: "",
        vendor: false,
        year_established: null,
        zip: "",
        product_categories: [],
        specialties: [],
      },
      states: [],
      cities: [],
      debounceTimer: null,
      backOfficeMember: {},
      searchFocus: false,
      mfr_company_query: "",
      setData: false,
      selectedLogo: null,
      logoUploading: false,
      contacts: null,
      currentMember: null,
      currentContactId: null,
      contactColumns: [
        { name: "Contact" },
        { name: "Email" },
        { name: "Username" },
        { name: "Manufacturers" },
        { name: "Actions", classes: "w-[50px]" },
      ],
    };
  },
  created() {
    this.id = this.$route.params.id;
  },
  mounted() {
    this.getMember();
  },
  methods: {
    async getMember() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/member/${this.id}`);
        if (data && data.member) {
          this.memberData = data.member;
          this.contacts = data.contacts;
          this.backOfficeMember = data.back_office_member
            ? data.back_office_member
            : null;
        }
        this.setData = true;
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async handleMember() {
      this.loaderStore.setIsLoading(true);
      try {
        // backOfficeMember can be null (non-admins, or members whose ES doc
        // doesn't carry that field). Only mirror network_subscriber when we
        // actually have a back-office record to write to.
        if (this.backOfficeMember) {
          this.backOfficeMember.network_subscriber =
            this.memberData.network_subscriber;
        }
        const payload = {
          member: this.memberData,
          back_office_member: this.backOfficeMember,
        };
        if (!this.isAdmin) {
          delete payload.back_office_member;
        }
        const { data } = await ApiRequest().post(`/member`, payload);
        if (data) {
          this.toastStore.show("Member updated successfully!", "success");
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getLocations() {
      if (!this.memberData.country || !this.memberData.zip) {
        this.states = [];
        this.cities = [];
        return;
      }
      try {
        const payload = {
          country_code: this.$getCountryCode(
            this.countries,
            this.memberData.country,
          ),
          postal_code: this.memberData.zip,
        };
        const response = await fetch("/public_api/postal_codes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          this.toastStore.show("Failed to fetch location data", "error");
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show("No location data found", "error");
          this.states = [];
          this.cities = [];
          return;
        }
        const statesMap = new Map();
        const citiesMap = new Map();
        data.forEach((item) => {
          const source = item._source;
          if (source.admin_code1) {
            statesMap.set(source.admin_code1, {
              code: source.admin_code1,
              name: source.admin_name1 || source.admin_code1,
            });
          }
          if (source.place_name) {
            citiesMap.set(source.place_name, {
              name: source.place_name,
            });
          }
        });
        this.states = Array.from(statesMap.values());
        this.cities = Array.from(citiesMap.values());
      } catch (e) {
        this.handleError(e);
        this.states = [];
        this.cities = [];
      }
    },
    debouncedGetLocations() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.getLocations();
      }, 500);
    },
    addContact() {
      this.currentMember = this.memberData;
    },
    closeAddContact() {
      this.currentMember = null;
    },
    confirmDelete(id) {
      this.currentContactId = id;
    },
    recordDeleted() {
      if (this.contacts) {
        const index = this.contacts.findIndex(
          (c) => c.contact_id === this.currentContactId,
        );
        if (index !== -1) this.contacts.splice(index, 1);
      }
      this.currentContactId = null;
      this.toastStore.show("Contact deleted successfully!", "success");
    },
    removeSelectedMfr(mfr_id) {
      this.memberData.mfrs.find((mfr) => mfr.mfr_id === mfr_id).selected =
        false;
    },
    onLogoChosen(ev) {
      this.selectedLogo = (ev.target.files && ev.target.files[0]) || null;
    },
    async uploadLogo() {
      if (!this.selectedLogo || !this.memberData.member_id) return;
      this.logoUploading = true;
      const form = new FormData();
      form.append("file", this.selectedLogo);
      try {
        const { data } = await ApiRequest().post(
          `/admin/member_logo/${this.memberData.member_id}`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        // The returned URL already includes a ?v=<ts> cache buster.
        this.memberData.logo_url = data.logo_url;
        this.selectedLogo = null;
        if (this.$refs.logoInput) this.$refs.logoInput.value = "";
        this.toastStore.show(
          "Logo uploaded. Click Update information to save.",
          "info",
        );
      } catch (e) {
        this.toastStore.show(
          (e.response && e.response.data && e.response.data.error) ||
            "Logo upload failed",
          "error",
        );
      } finally {
        this.logoUploading = false;
      }
    },
    removeLogo() {
      // Clears the stored URL; the blob in Azure stays orphaned but harmless
      // until a future re-upload overwrites it. Save still needs to be clicked.
      this.memberData.logo_url = "";
      this.selectedLogo = null;
      if (this.$refs.logoInput) this.$refs.logoInput.value = "";
      this.toastStore.show(
        "Logo cleared. Click Update information to save.",
        "info",
      );
    },
    updateSelectedProductCategories(value) {
      this.memberData.product_categories = value;
    },
    updateSelectedSpecialties(value) {
      this.memberData.specialties = value;
    },
  },
  watch: {
    "memberData.country": function (newVal) {
      if (newVal && this.memberData.zip) {
        this.debouncedGetLocations();
      }
    },
    "memberData.zip": function (newVal) {
      if (newVal && this.memberData.country) {
        this.debouncedGetLocations();
      }
    },
  },
  computed: {
    filteredManufactuers() {
      const mfrs = this.memberData?.mfrs;
      if (!mfrs?.length) {
        return [];
      }
      return mfrs.filter((mfr) =>
        mfr.company
          .toLowerCase()
          .includes(this.mfr_company_query.toLowerCase()),
      );
    },
    selectedManufactuers() {
      if (!this.memberData?.mfrs?.length) {
        return [];
      }
      return this.memberData.mfrs?.filter((mfr) => mfr.selected) || [];
    },
    loggedInUser() {
      return this.userStore.user;
    },
    contact() {
      return this.loggedInUser.contact;
    },
    isAdmin() {
      return this.loggedInUser.global_admin;
    },
    canManageContacts() {
      // Global admins always; member administrators when they're administering
      // their own company (the page itself is already gated to those two cases
      // via SettingsView's canEditMember check).
      return this.isAdmin || this.contact?.administrator === true;
    },
    countries() {
      return this.countriesStore.countries;
    },
    checkboxes: {
      get() {
        return {
          assoc_member: this.memberData.assoc_member,
          network_subscriber: this.memberData.network_subscriber,
        };
      },
      set(val) {
        this.memberData.assoc_member = val.assoc_member;
        this.memberData.network_subscriber = val.network_subscriber;
      },
    },
  },
};
</script>
